import React from "react";
import {
  ScatterplotLayer,
  GeoJsonLayer,
  IconLayer,
  TextLayer,
} from "@deck.gl/layers";
import { HexagonLayer, HeatmapLayer } from "@deck.gl/aggregation-layers";
import { EditableGeoJsonLayer, SelectionLayer } from "nebula.gl";
import Constant from "constants/index";

type MapLayerProps = {
  state: any;
  mapDrawingEditHandler: (data: any) => void;
  onNearbyLocationItemClick: (data: any) => void;
  onNearbyLocationItemHover: (data: any) => void;
  onWardItemHover: (data: any) => void;
  onExternalRetailDemandItemHover: (data: any) => void;
  onPlanningPolygonItemClick: (data: any) => void;
  onPlanningPolygonItemHover: (data: any) => void;
  setSelectedFeatureIndexes: (data: any) => void;
  onTrafficCountingPointHover: (data: any) => void;
};

const MapLayer = ({
  state,
  mapDrawingEditHandler,
  onNearbyLocationItemClick,
  onNearbyLocationItemHover,
  onWardItemHover,
  onExternalRetailDemandItemHover,
  onPlanningPolygonItemClick,
  onPlanningPolygonItemHover,
  setSelectedFeatureIndexes,
  onTrafficCountingPointHover,
}: MapLayerProps) => {
  const {
    currentLocation,
    drawingGeoJson,
    selectedFeatureIndexes,
    planningPolygons,
    wards,
    foodAndBeverageTransactions,
    foodAndBeverageDeliveryTransactions,
    staticDemandTransactions,
    externalRetailDemandDetail,
    mapMode,
    mapSettings,
    isEnableSelection,
    showPlanningPolygon,
    nearbyLocations,
    currentUserBrandSites,
    mapStyle,
    isShowTrafficCounting,
    trafficCountingData,
  } = state;

  const mapLayers = [];
  const editableGeoJsonLayerOpt: any = {
    id: "geojson",
    data: drawingGeoJson,
    mode: mapMode,
    selectedFeatureIndexes,
    onEdit: mapDrawingEditHandler,
    getFillColor: [0, 153, 255, 255],
    getLineColor: [0, 153, 255, 255],
    editHandleType: "point",
    opacity: 0.1,
  };
  const editableGeoJsonLayer = new EditableGeoJsonLayer(
    // @ts-ignore
    editableGeoJsonLayerOpt
  );
  mapLayers.push(editableGeoJsonLayer);
  const iconLayerOpt: any = {
    id: "current-location-layer-icon",
    data: [currentLocation],
    opacity: 1,
    stroked: true,
    filled: true,
    radiusScale: 1,
    radiusMinPixels: 10,
    radiusMaxPixels: 20,
    lineWidthMinPixels: 4,
    getPosition: (d: any) => [d.longitude, d.latitude],
    getIcon: (d: any) => {
      return {
        url: `${process.env.PUBLIC_URL}/images/current_location_pin.png`,
        width: 54,
        height: 54,
      };
    },
    getSize: 54,
    sizeScale: 1,
  };
  const currentLocationLayerIcon = new IconLayer(iconLayerOpt);
  mapLayers.push(currentLocationLayerIcon);
  if (nearbyLocations) {
    const nearbyLocationLayer = new ScatterplotLayer({
      id: "nearby-location-layer",
      data: nearbyLocations.filter(
        (nearbyLocation: any) =>
          (nearbyLocation.is_competitor === 0 &&
            nearbyLocation.is_current_user_brand === 0) ||
          !nearbyLocation.brand.brand_logo_image
      ),
      pickable: true,
      stroked: true,
      filled: true,
      radiusScale: 1,
      radiusMinPixels: 5,
      radiusMaxPixels: 10,
      lineWidthMinPixels: 4,
      opacity: 0.4,
      getPosition: (d: any) => [d.longitude, d.latitude],
      getRadius: 5,
      getFillColor: (d: any) => d.rgba_color_normalized,
      getLineColor: (d: any) => d.rgba_color_normalized,
      onClick: onNearbyLocationItemClick,
      onHover: onNearbyLocationItemHover,
    });
    mapLayers.push(nearbyLocationLayer);
    const iconLayerOpt: any = {
      id: "competitor-location-layer",
      data: nearbyLocations.filter(
        (nearbyLocation: any) =>
          (nearbyLocation.is_competitor === 1 ||
            nearbyLocation.is_current_user_brand === 1) &&
          nearbyLocation.brand.brand_logo_image
      ),
      pickable: true,
      stroked: true,
      filled: true,
      radiusScale: 1,
      radiusMinPixels: 10,
      radiusMaxPixels: 20,
      lineWidthMinPixels: 4,
      opacity: 0.6,
      getIcon: (d: any) => {
        let isSite = false;
        let imageUrl = d.brand.brand_map_image || d.brand.brand_logo_image;
        let imageSize = 32;
        if (d.is_current_user_brand === 1 && d.site_id && !d.store_id) {
          isSite = true;
          imageUrl =
            d.brand.brand_site_map_image ||
            d.brand.brand_map_image ||
            d.brand.brand_logo_image;
        }
        return {
          url: imageUrl,
          width: imageSize,
          height: imageSize,
        };
      },
      getSize: 40,
      sizeScale: 1.2,
      getPosition: (d: any) => [d.longitude, d.latitude],
      onClick: onNearbyLocationItemClick,
      onHover: onNearbyLocationItemHover,
    };
    const nearbyCompetitorLocationLayer = new IconLayer(iconLayerOpt);
    mapLayers.push(nearbyCompetitorLocationLayer);
  }
  if (currentUserBrandSites && currentUserBrandSites.length > 0) {
    const iconLayerOpt: any = {
      id: "current-user-brand-sites-layer",
      data: currentUserBrandSites,
      pickable: true,
      stroked: true,
      filled: true,
      radiusScale: 1,
      radiusMinPixels: 10,
      radiusMaxPixels: 20,
      lineWidthMinPixels: 4,
      opacity: 0.6,
      getIcon: (d: any) => {
        return {
          url: d.brand.brand_logo_image,
          width: 32,
          height: 32,
        };
      },
      getSize: 32,
      sizeScale: 1,
      getPosition: (d: any) => [d.longitude, d.latitude],
      onClick: onNearbyLocationItemClick,
      onHover: onNearbyLocationItemHover,
    };
    const currentUserBrandSitesLayer = new IconLayer(iconLayerOpt);
    mapLayers.push(currentUserBrandSitesLayer);
  }
  if (wards && mapSettings.showWardBoundary) {
    mapLayers.push(
      new GeoJsonLayer({
        id: "ward-geojson-layer",
        data: wards
          .filter((ward: any) => ward.geojson)
          .map((ward: any) => {
            if (ward.geojson) {
              const object = {
                ...ward.geojson,
                ...ward,
                properties: {
                  name: ward.name,
                  fill_color: ward.fill_color,
                  line_color: ward.line_color,
                },
              };
              return object;
            }
          }),
        pickable: true,
        stroked: true,
        opacity: 0.3,
        filled: mapSettings.showWardDensity,
        lineWidthScale: 2,
        lineWidthMinPixels: 1,
        getFillColor: (d: any) => Constant.COLOR_SCALE(d.population / d.area),
        getLineColor: (d: any) => d.properties.line_color,
        getLineWidth: 1,
        getElevation: 1,
        onHover: onWardItemHover,
      })
    );
  }

  // Draw hexagon for retail data
  if (externalRetailDemandDetail) {
    const hexagonLayerColorRange: any = Constant.COLOR_RANGE.SKY_ORANGE_RANGE;
    const externalRetailDemandDetailLayer = new HexagonLayer({
      id: "retail-demand-hexagon-layer",
      data: externalRetailDemandDetail,
      radius: 100,
      elevationScale: 1,
      getPosition: (d: any) => [d.longitude, d.latitude],
      colorRange: hexagonLayerColorRange,
      opacity: 0.7,
      pickable: true,
      onHover: onExternalRetailDemandItemHover,
    });
    mapLayers.push(externalRetailDemandDetailLayer);
  }

  // Draw hexagon for FnB data
  if (foodAndBeverageTransactions) {
    const hexagonLayerColorRange: any = Constant.COLOR_RANGE.BLUE_RANGE;
    const foodAndBeverageTransactionsLayer = new HexagonLayer({
      id: "fnb-transaction-hexagon-layer",
      data: foodAndBeverageTransactions,
      radius: 50,
      elevationScale: 1,
      getPosition: (d: any) => [d.longitude, d.latitude],
      colorRange: hexagonLayerColorRange,
      opacity: 0.6,
    });
    mapLayers.push(foodAndBeverageTransactionsLayer);
  }

  // Draw hexagon for FnB DELIVERY data
  if (foodAndBeverageDeliveryTransactions) {
    const heatMapLayerColorRange: any = Constant.COLOR_RANGE.ORANGE_RANGE;
    const heatMapLayerOpt: any = {
      id: "fnb-delivery-transaction-hexagon-layer",
      data: foodAndBeverageDeliveryTransactions,
      radius: 50,
      elevationScale: 1,
      getPosition: (d: any) => [d.longitude, d.latitude],
      colorRange: heatMapLayerColorRange,
      opacity: 0.5,
    };
    const foodAndBeverageDeliveryTransactionsLayer = new HeatmapLayer(
      heatMapLayerOpt
    );
    mapLayers.push(foodAndBeverageDeliveryTransactionsLayer);
  }

  // Draw heatmap for static demand
  if (staticDemandTransactions) {
    const heatMapLayerColorRange: any = Constant.COLOR_RANGE.ORANGE_RANGE;
    const heatMapLayerOpt: any = {
      id: "static-demand-transaction-heatmap-layer",
      data: staticDemandTransactions,
      radius: 50,
      getPosition: (d: any) => [d.longitude, d.latitude],
      colorRange: heatMapLayerColorRange,
      opacity: 0.5,
    };
    const staticDemandTransactionsLayer = new HeatmapLayer(heatMapLayerOpt);
    mapLayers.push(staticDemandTransactionsLayer);
  }

  // Draw GeoJSON for planning zones
  const planningPolygonLayerIds: Array<any> = [];
  if (planningPolygons && planningPolygons.length > 0 && showPlanningPolygon) {
    planningPolygons.forEach((planningPolygon: any) => {
      const layerId = `planning-zone-geojson-${planningPolygon.properties.id}`;
      planningPolygonLayerIds.push(layerId);

      let polygonOpacity = 0.1;
      let getLineWidth = 1;
      let getFillColor: any = [30, 144, 255, 255];
      let textColor: any = ["Dark", "Satellite"].includes(mapStyle.title)
        ? [255, 255, 255, 255]
        : [0, 0, 0, 255];

      selectedFeatureIndexes.forEach((eachSelectedFeatureIndex: any) => {
        if (eachSelectedFeatureIndex.layer.id.includes(layerId)) {
          polygonOpacity = 0.6;
          getLineWidth = 3;
          getFillColor = [102, 0, 204, 255];
          textColor = [255, 255, 255, 255];
        }
      });
      const planningPolygonLayer = new GeoJsonLayer({
        id: layerId,
        data: planningPolygon,
        pickable: true,
        stroked: true,
        filled: true,
        lineWidthScale: 2,
        lineWidthMinPixels: 1,
        opacity: polygonOpacity,
        getFillColor,
        getLineColor: [30, 144, 255, 255],
        getLineWidth: getLineWidth,
        getElevation: 1,
        onClick: onPlanningPolygonItemClick,
        onHover: onPlanningPolygonItemHover,
      });
      mapLayers.push(planningPolygonLayer);

      if (mapSettings.showPlanningZoneTitle) {
        const textLayerOpt: any = {
          id: `text${layerId}`,
          data: [planningPolygon],
          pickable: true,
          getPosition: (d: any) => d.properties.centroid,
          getText: (d: any) =>
            `${d.properties.title_no_accent}\n\n${d.properties.user.first_name} ${d.properties.user.last_name}`,
          getSize: 18,
          getColor: textColor,
          getTextAnchor: "middle",
          getAlignmentBaseline: "center",
          sizeScale: 1,
          fontFamily: "Arial",
          onClick: onPlanningPolygonItemClick,
          onHover: onPlanningPolygonItemHover,
        };
        const planningTextLayer = new TextLayer(textLayerOpt);
        mapLayers.push(planningTextLayer);
      }
    });
  }

  // Selection layer
  if (isEnableSelection) {
    const selectionOpt: any = {
      id: "`planning-zone-geojson-selection",
      selectionType: "rectangle",
      onSelect: setSelectedFeatureIndexes,
      layerIds: planningPolygonLayerIds,
      getTentativeFillColor: () => [0, 255, 251, 50],
      getTentativeLineColor: () => [30, 144, 255, 255],
      getTentativeLineDashArray: () => [0, 0],
      getLineWidth: 1,
      lineWidthMinPixels: 1,
    };
    const selectionPlanningPolygon = new SelectionLayer(
      // @ts-ignore
      selectionOpt
    );
    mapLayers.push(selectionPlanningPolygon);
  }

  // Traffic counting layer
  if (isShowTrafficCounting && trafficCountingData) {
    // Non-competitor location
    const trafficCountingPointsLayer = new ScatterplotLayer({
      id: "traffic-counting-points-layer",
      data: trafficCountingData,
      pickable: true,
      stroked: true,
      filled: true,
      radiusScale: 1,
      radiusMinPixels: 10,
      radiusMaxPixels: 20,
      lineWidthMinPixels: 4,
      opacity: 0.8,
      getPosition: (d: any) => [d.longitude, d.latitude],
      getRadius: 4,
      getFillColor: [0, 255, 251],
      getLineColor: [0, 255, 251],
      onHover: onTrafficCountingPointHover,
    });
    mapLayers.push(trafficCountingPointsLayer);
  }
  return mapLayers;
};

export default MapLayer;
